import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Verifica se a data é válida
    if (isNaN(date.getTime())) {
      return 'Data inválida';
    }
    
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return dateString;
  }
}

/**
 * Format a number as currency
 */
export function formatCurrency(value: number, currency: string = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Format document numbers like CNPJ (xx.xxx.xxx/xxxx-xx) or CPF (xxx.xxx.xxx-xx)
 */
export function formatDocument(doc: string | null | undefined): string {
  if (!doc) return '';
  
  // Remove non-numeric characters
  const numbers = doc.replace(/[^\d]/g, '');
  
  // CNPJ: xx.xxx.xxx/xxxx-xx
  if (numbers.length === 14) {
    return numbers.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  }
  
  // CPF: xxx.xxx.xxx-xx
  if (numbers.length === 11) {
    return numbers.replace(
      /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
      '$1.$2.$3-$4'
    );
  }
  
  return doc;
}

export function formatDateTime(date: string | null | undefined) {
  if (!date) return '-';
  
  try {
    const dateObj = new Date(date);
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return '-';
    }
    
    // Using a custom format to remove the comma
    const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    
    const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
    
    const formattedDate = dateFormatter.format(dateObj);
    const formattedTime = timeFormatter.format(dateObj);
    
    return `${formattedDate} ${formattedTime}`;
  } catch (error) {
    console.error('Error formatting date and time:', error);
    return '-';
  }
}

export function formatShortMonthName(monthIndex: number): string {
  try {
    // Create a date object for the given month (year and day don't matter)
    const date = new Date(2024, monthIndex); 
    
    // Check if the month index is valid
    if (monthIndex < 0 || monthIndex > 11 || isNaN(date.getTime())) {
      console.warn('Invalid month index provided:', monthIndex);
      // Return a placeholder or throw an error, depending on desired handling
      return 'INV'; 
    }
    
    return date.toLocaleString('pt-BR', { month: 'short' }).toUpperCase();
  } catch (error) {
    console.error('Error formatting short month name:', error);
    // Fallback for any unexpected errors
    return 'ERR'; 
  }
}
